import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  FileText, 
  Bell, 
  Archive, 
  Radio, 
  Search,
  ChevronRight,
  Type
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Footer } from '../components/Footer';
import { CompactHeader } from '../components/CompactHeader';


export default function CauseList() {
  const [textSize, setTextSize] = useState('normal');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const causeListCards= [
    {
      id: 'daily',
      title: 'Daily List',
      description: "View today's court listings",
      icon: <Calendar className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/daily',
      ariaLabel: 'View Daily Cause List'
    },
    {
      id: 'advance',
      title: 'Advance List',
      description: 'View upcoming court schedules',
      icon: <FileText className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/advance',
      ariaLabel: 'View Advance Cause List'
    },
    {
      id: 'supplementary',
      title: 'Supplementary List',
      description: 'Check recently added cases',
      icon: <FileText className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/supplementary',
      ariaLabel: 'View Supplementary Cause List'
    },
    {
      id: 'notice',
      title: 'Notice',
      description: 'View official court notices',
      icon: <Bell className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/notice',
      ariaLabel: 'View Court Notices'
    },
    {
      id: 'archives',
      title: 'Archives',
      description: 'Access previous cause lists',
      icon: <Archive className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/archives',
      ariaLabel: 'Access Cause List Archives'
    },
    {
      id: 'live',
      title: 'Live Status',
      description: 'Track live cause list updates',
      icon: <Radio className="w-12 h-12 text-blue-600" />,
      link: '/cause-list/live',
      ariaLabel: 'View Live Cause List Status'
    }
  ];

  const adjustTextSize = (size) => {
    setTextSize(size);
  };

  const getFontSizeClass = () => {
    if (textSize === 'large') return 'text-lg';
    if (textSize === 'small') return 'text-sm';
    return 'text-base';
  };

  const handleSearch = () => {
  if (searchQuery.trim() !== "") {
    navigate(`/cause-list/search?q=${encodeURIComponent(searchQuery)}`);
  }
};

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <CompactHeader  />
      {/* Accessibility Controls Bar */}
      <div className="bg-[#1E293B] text-white py-3 px-4 sticky top-[100px] z-40">
        <div className="max-w-7xl mx-auto flex justify-end items-center">
          {/* Text Size Controls */}
          <div className="flex items-center gap-3">
            <Type className="w-5 h-5" aria-hidden="true" />
            <div className="flex gap-2" role="group" aria-label="Text size controls">
              <button
                onClick={() => adjustTextSize('small')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'small' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Small text size"
                aria-pressed={textSize === 'small'}
              >
                A-
              </button>
              <button
                onClick={() => adjustTextSize('normal')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'normal' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Normal text size"
                aria-pressed={textSize === 'normal'}
              >
                A
              </button>
              <button
                onClick={() => adjustTextSize('large')}
                className={`px-3 py-2 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 ${
                  textSize === 'large' ? 'bg-white text-[#1E293B]' : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label="Large text size"
                aria-pressed={textSize === 'large'}
              >
                A+
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section with Gradient Background */}
      <section className="relative py-24 bg-hero-light-image dark:bg-hero-dark overflow-hidden">
        {/* Subtle overlay for better text readability */}
        <div className="absolute inset-0 bg-black/10"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Cause List
            </h1>
            <p className={`${getFontSizeClass()} text-gray-800 max-w-3xl mx-auto drop-shadow-sm`}>
              Search by Suit Number, Party Name, or Judge.
            </p>
          </div>

          {/* Search Bar with Button */}
       <div className="max-w-2xl mx-auto">
  <div className="flex gap-3">
    <div className="flex-1">
      <Input
        type="text"
        placeholder="Search by Suit Number, Party Name, or Judge Name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onKeyPress={handleKeyPress}
        className="w-full px-4 py-4 h-14 rounded-xl bg-white text-gray-900 border-0 focus:ring-2 focus:ring-[#155dfc]/50 placeholder:text-gray-500"
        aria-label="Search cause list"
      />
    </div>
    <Button
      onClick={handleSearch}
      className="h-14 px-8  text-white rounded-xl focus:ring-2"
      aria-label="Search"
    >
     <Search className="mr-2 w-5 h-5" /> Search
    </Button>
  </div>
</div>

        </div>
      </section>

      {/* Main Navigation Grid */} 
      <section className="py-16 px-4 bg-gray-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {causeListCards.map((card, index) => (
              <Link
                key={card.id}
                to={card.link}
                className="group bg-white dark:bg-slate-700 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 p-8 flex flex-col items-center text-center border-2 border-transparent hover:border-[#155dfc] focus:outline-none focus:ring-4 focus:ring-[#155dfc]/30 min-h-[280px]"
                aria-label={card.ariaLabel}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.currentTarget.click();
                  }
                }}
              >
                <div className="text-[#155dfc] mb-6 group-hover:scale-110 transition-transform duration-300">
                  {card.icon}
                </div>
                <h3 className={`${getFontSizeClass()} text-gray-900 dark:text-white mb-3`}>
                  {card.title}
                </h3>
                <p className={`${getFontSizeClass()} text-gray-600 dark:text-gray-300 mb-4 flex-grow`}>
                  {card.description}
                </p>
                <div className="flex items-center gap-2 text-[#155dfc] group-hover:gap-3 transition-all">
                  <span className={`${getFontSizeClass()}`}>
                    Access
                  </span>
                  <ChevronRight className="w-5 h-5" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
 <Footer />
    </div>
  );
}
